import { orderDateParts, tryGetUTCDate } from '@/helpers/DateFormatter';

/**
 * Ren formaterings-kjerne for user-prefs-styrt tall- og datovisning (WI 2086).
 * Ingen Vue/Pinia-avhengighet: separatorer, datoformat og tidssone kommer inn som
 * parametre. Bindes mot prefs av `useUserPrefFormatting` (produksjon) og av
 * test-doblen `tests/testUtils/userPrefFormattingMock.ts`, slik at begge garantert
 * deler samme adferd.
 */

export interface FormatNumberOptions {
  /** Tusenskille av/på (default på, jf. `toLocaleString`). */
  useGrouping?: boolean;
  minimumFractionDigits?: number;
  /** Default 3 — paritet med `Number.prototype.toLocaleString`. */
  maximumFractionDigits?: number;
}

/** Rå pref-felter slik de ligger i `userPreferences` (ulastet/delvis utfylt er lovlig). */
export interface UserPrefFormatPrefs {
  dateTimeFormat?: string | null;
  numberFormatThousandsSeparator?: string | null;
  numberFormatDecimalSeparator?: string | null;
  timezone?: string | null;
}

/** Oppløste formaterings-innstillinger, med defaults der prefs mangler. */
export interface UserPrefFormatSettings {
  /** 'DMY' | 'MDY' | 'YMD'. */
  dateFormat: string;
  thousandsSeparator: string;
  decimalSeparator: string;
  /** IANA-tidssone. */
  timeZone: string;
}

/**
 * Løser rå prefs til komplette innstillinger med produkt-defaults:
 * 'DMY' (norsk default), mellomrom/komma, 'Europe/Oslo' (jf. RegionalSettings).
 */
export function resolveUserPrefFormatSettings (prefs: UserPrefFormatPrefs | null | undefined): UserPrefFormatSettings {
  return {
    dateFormat: (nonEmpty(prefs?.dateTimeFormat) ?? 'DMY').toUpperCase(),
    thousandsSeparator: nonEmpty(prefs?.numberFormatThousandsSeparator) ?? ' ',
    decimalSeparator: nonEmpty(prefs?.numberFormatDecimalSeparator) ?? ',',
    timeZone: nonEmpty(prefs?.timezone) ?? 'Europe/Oslo'
  };
}

function nonEmpty (raw: string | null | undefined): string | null {
  return typeof raw === 'string' && raw.length > 0 ? raw : null;
}

/**
 * Formaterer et tall med gitt tusen-/desimalskille. Ikke-endelige verdier
 * (NaN/Infinity) returneres verbatim, som `toLocaleString` ville gjort.
 */
export function formatNumberCore (value: number, settings: UserPrefFormatSettings, options: FormatNumberOptions = {}): string {
  if (!Number.isFinite(value)) return String(value);
  // Locale her er kun en stabil kilde til sifre/gruppering; separatorene
  // byttes ut med innstillingenes separatorer per part.
  const parts = new Intl.NumberFormat('en-US', {
    useGrouping: options.useGrouping ?? true,
    minimumFractionDigits: options.minimumFractionDigits,
    maximumFractionDigits: options.maximumFractionDigits ?? 3
  }).formatToParts(value);
  return parts
    .map((part) => {
      if (part.type === 'group') return settings.thousandsSeparator;
      if (part.type === 'decimal') return settings.decimalSeparator;
      return part.value;
    })
    .join('');
}

// A date to render: an ISO string from the wire, a Date already parsed, or nothing.
type FormattableDate = string | Date | null | undefined;

/** Dato uten klokkeslett, etter gitt datoformat og tidssone. */
export function formatDateCore (value: FormattableDate, settings: UserPrefFormatSettings): string {
  return formatInTimeZone(value, settings, false);
}

/** Dato + klokkeslett (HH:mm), etter gitt datoformat og tidssone. */
export function formatDateTimeCore (value: FormattableDate, settings: UserPrefFormatSettings): string {
  return formatInTimeZone(value, settings, true);
}

function formatInTimeZone (value: FormattableDate, settings: UserPrefFormatSettings, withTime: boolean): string {
  if (value === null || value === undefined || value === '') return '';
  let date: Date | null;
  if (value instanceof Date) {
    date = Number.isNaN(value.getTime()) ? null : value;
  } else {
    date = tryGetUTCDate(value);
  }
  // Uparsbar verdi vises verbatim (presentasjonsfallback, jf. DateFormatter.formatDate).
  if (!date) return String(value);

  // Locale her er kun part-uttrekk; rekkefølgen settes av datoformat-innstillingen.
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: settings.timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(date);
  const lookup = Object.fromEntries(parts.map((part) => [part.type, part.value]));

  const datePart = orderDateParts(lookup.day ?? '', lookup.month ?? '', lookup.year ?? '', settings.dateFormat);
  return withTime ? `${datePart} ${lookup.hour ?? '00'}:${lookup.minute ?? '00'}` : datePart;
}
